import { Box, Typography } from '@mui/material';
import React from 'react';
import { Outlet, useParams } from 'react-router';

import { useGetContactsQuery } from '@/api/v1';
import AddIcon from '@/assets/icons/add.svg?react';
import { AddObjectButton } from '@/components/molecules/AddObjectButton';
import { FiatPaymentMethodCard } from '@/components/organisms/FiatPaymentMethodCard';
import { useCustomer } from '@/hooks/useCustomer';
import { useTranslator } from '@/hooks/useTranslator';
import { Contact } from '@/types/contacts';
import { Page } from '@/types/page';
import { RecipientResponse } from '@/types/recipient';

export const RecipientPaymentMethods: React.FC = (): React.ReactElement => {
  const customer = useCustomer();

  const { contactId = '' } = useParams();
  const { data: recipientsPage = Page.empty<Contact>() } = useGetContactsQuery(
    { beneficiaryId: customer.beneficiaryId, size: 1000 },
    { skip: !customer.beneficiaryId },
  );
  const recipients = recipientsPage.content;

  const tr = useTranslator();
  const recipient = React.useMemo(
    (): Contact | undefined =>
      recipients.find((recipient: Contact): boolean => recipient.id === contactId),
    [recipients, contactId],
  );

  // Sort recipients by id to maintain stable order across refetches
  const sortedRecipients = React.useMemo(
    (): readonly RecipientResponse[] =>
      recipient?.recipients
        ? [...recipient.recipients].sort((a: RecipientResponse, b: RecipientResponse): number =>
            a.id.localeCompare(b.id),
          )
        : [],
    [recipient?.recipients],
  );

  return (
    <Box px={4} py={4}>
      <Typography fontSize={20} fontWeight={500} lineHeight="32px">
        {tr('PAYMENT_METHODS')}
      </Typography>
      <Box display="flex" flexDirection="column" gap={2} mt={3}>
        {sortedRecipients.map(
          (r: RecipientResponse): React.ReactElement => (
            <FiatPaymentMethodCard key={r.id} paymentMethod={r} />
          ),
        )}
      </Box>
      <Box mt={5}>
        <AddObjectButton path="new" label={tr('ADD_PAYMENT_METHOD')} icon={AddIcon} />
      </Box>

      {/* For modals */}
      <Outlet />
    </Box>
  );
};
